import React, { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { authenticatedFetch } from '@/lib/authenticatedFetch';

interface JourneyProgress {
  currentStep: number;
  overallCompletionPercentage: number;
  step1OverviewCompleted: boolean;
  step2MyJourneyCompleted: boolean;
  step3OrgNarrativeCompleted: boolean;
  step4FractalsCompleted: boolean;
  step5SimulationCompleted: boolean;
  step6ActionFlowCompleted: boolean;
  lastLoginDate: string;
  createdAt: string;
}

interface NotificationTemplate {
  trigger: string;
  title: string;
  message: string;
  type: 'journey_milestone' | 'component_unlock' | 'encouragement' | 'reminder' | 'system';
  category: 'progress' | 'achievement' | 'tip' | 'warning' | 'celebration';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  emotionalTone: 'encouraging' | 'celebratory' | 'gentle' | 'urgent';
  journeyStep?: number;
  componentType?: 'life' | 'money' | 'joy' | 'goodwill';
  actionText?: string;
  actionUrl?: string;
  delayMinutes?: number;
}

const notificationTemplates: NotificationTemplate[] = [
  // Welcome and Getting Started
  {
    trigger: 'registration_complete',
    title: 'Welcome aboard, pilot! 🚁',
    message: 'Your helicopter awaits! You\'ve successfully registered and are ready to begin building your leadership helicopter.',
    type: 'journey_milestone',
    category: 'celebration',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 1,
    actionText: 'Start Your Journey',
    actionUrl: '/journey/step1'
  },
  
  // Step Completions
  {
    trigger: 'step1_completed',
    title: 'Pre-Flight Complete! ✈️',
    message: 'You\'ve completed the Pre-Flight phase and are ready for Identity Transformation. Your helicopter components are waiting!',
    type: 'journey_milestone',
    category: 'achievement',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 2,
    actionText: 'Continue to Identity Transformation',
    actionUrl: '/journey/step2'
  },
  
  {
    trigger: 'step2_completed',
    title: 'Identity Transformation Complete! 🎯',
    message: 'Excellent! You\'ve embraced your agency and are ready to build your LIFE component through kindness stories.',
    type: 'journey_milestone',
    category: 'achievement',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 3,
    componentType: 'life',
    actionText: 'Build Your LIFE Component',
    actionUrl: '/journey/step3'
  },
  
  {
    trigger: 'step3_completed',
    title: 'LIFE Component Unlocked! 🦔',
    message: 'Your helicopter body is complete! Your kindness stories have formed the foundation of your leadership helicopter. Time for the MONEY component.',
    type: 'component_unlock',
    category: 'achievement',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 4,
    componentType: 'money',
    actionText: 'Build Your MONEY Component',
    actionUrl: '/journey/step4'
  },
  
  {
    trigger: 'step4_completed',
    title: 'MONEY Component Unlocked! 💰',
    message: 'Your helicopter controls are ready! You\'ve mastered the lemonade stand and understand how money works as an agency tool. Time for JOY!',
    type: 'component_unlock',
    category: 'achievement',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 5,
    componentType: 'joy',
    actionText: 'Build Your JOY Component',
    actionUrl: '/journey/step5'
  },
  
  {
    trigger: 'step5_completed',
    title: 'JOY Component Complete! 🎉',
    message: 'Your main rotor is spinning! You\'ve earned your License to Fly Free and understand authentic leadership. Ready for takeoff!',
    type: 'component_unlock',
    category: 'achievement',
    priority: 'high',
    emotionalTone: 'celebratory',
    journeyStep: 6,
    componentType: 'goodwill',
    actionText: 'Complete Your Journey',
    actionUrl: '/journey/step6'
  },
  
  {
    trigger: 'journey_complete',
    title: 'Helicopter Complete! 🚁✨',
    message: 'Congratulations! You\'ve built your complete leadership helicopter and earned your pilot license. Your agency journey continues!',
    type: 'journey_milestone',
    category: 'celebration',
    priority: 'urgent',
    emotionalTone: 'celebratory',
    actionText: 'View Your Achievements',
    actionUrl: '/journey/complete'
  },
  
  // Encouragement and Tips
  {
    trigger: 'daily_encouragement',
    title: 'Your helicopter is waiting! 💪',
    message: 'Every great pilot needs practice. Take a moment today to continue building your leadership helicopter.',
    type: 'encouragement',
    category: 'tip',
    priority: 'medium',
    emotionalTone: 'encouraging',
    actionText: 'Continue Journey',
    actionUrl: '/journey'
  },
  
  {
    trigger: 'inactivity_3_days',
    title: 'Your helicopter misses you! 🚁',
    message: 'It\'s been a few days since your last visit. Your leadership journey is waiting - even 5 minutes can make a difference.',
    type: 'reminder',
    category: 'tip',
    priority: 'medium',
    emotionalTone: 'gentle',
    actionText: 'Resume Journey',
    actionUrl: '/journey'
  },
  
  {
    trigger: 'inactivity_7_days',
    title: 'We believe in you! 🌟',
    message: 'Building a helicopter takes time and consistency. You\'ve made great progress - let\'s keep your momentum going!',
    type: 'encouragement',
    category: 'tip',
    priority: 'high',
    emotionalTone: 'encouraging',
    actionText: 'Pick Up Where You Left Off',
    actionUrl: '/journey'
  },
  
  {
    trigger: 'stuck_on_step',
    title: 'Need a gentle push? 🤲',
    message: 'Sometimes the best stories come when we give ourselves permission to be imperfect. Your experiences matter.',
    type: 'encouragement',
    category: 'tip',
    priority: 'medium',
    emotionalTone: 'gentle',
    actionText: 'Get Help',
    actionUrl: '/help'
  },
  
  // Component-Specific Tips
  {
    trigger: 'life_component_tip',
    title: 'Kindness Story Tip 🦔',
    message: 'Remember: kindness includes being kind to yourself. Some of your most powerful stories might be small, everyday moments.',
    type: 'encouragement',
    category: 'tip',
    priority: 'low',
    emotionalTone: 'gentle',
    componentType: 'life'
  },
  
  {
    trigger: 'money_component_tip',
    title: 'Financial Agency Tip 💡',
    message: 'Money is simply a tool for agency. Focus on how financial understanding can help you serve others more effectively.',
    type: 'encouragement',
    category: 'tip',
    priority: 'low',
    emotionalTone: 'encouraging',
    componentType: 'money'
  },
  
  {
    trigger: 'joy_component_tip',
    title: 'Leadership Insight ✨',
    message: 'True leadership isn\'t about perfection - it\'s about authentic connection and helping others discover their own agency.',
    type: 'encouragement',
    category: 'tip',
    priority: 'low',
    emotionalTone: 'encouraging',
    componentType: 'joy'
  }
];

const SmartNotificationTrigger: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, isLoading } = useAuth();

  // Fetch journey progress - only when authenticated with additional safety checks
  const { data: progress } = useQuery({
    queryKey: ['/api/journey/progress'],
    refetchInterval: 60000, // Check every minute
    enabled: !!(isAuthenticated && user && !isLoading && user.id),
    retry: false // Don't retry failed requests to prevent 404 loops
  });

  // Fetch communication preferences - only when authenticated with additional safety checks
  const { data: preferences } = useQuery({
    queryKey: ['/api/communication-preferences'],
    enabled: !!(isAuthenticated && user && !isLoading && user.id),
    retry: false // Don't retry failed requests to prevent 404 loops
  });

  // Create notification mutation
  const createNotificationMutation = useMutation({
    mutationFn: async (notificationData: any) => {
      const response = await authenticatedFetch('notifications', {
        method: 'POST',
        body: JSON.stringify(notificationData)
      });
      if (!response.ok) throw new Error('Failed to create notification');
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread'] });
    }
  });

  // Track engagement mutation
  const trackEngagementMutation = useMutation({
    mutationFn: async (engagementData: any) => {
      const response = await authenticatedFetch('journey/engagement', {
        method: 'POST',
        body: JSON.stringify(engagementData)
      });
      if (!response.ok) throw new Error('Failed to track engagement');
      return response.json();
    }
  });

  const shouldTriggerNotification = (template: NotificationTemplate, progress: JourneyProgress): boolean => {
    const now = new Date();
    const lastLogin = new Date(progress.lastLoginDate || progress.createdAt);
    const daysSinceLastLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));
    
    // Check if notifications are enabled
    if (!preferences?.enableInAppNotifications) return false;
    
    // Check quiet hours
    if (preferences?.quietHoursStart && preferences?.quietHoursEnd) {
      const currentHour = now.getHours();
      const quietStart = parseInt(preferences.quietHoursStart.split(':')[0]);
      const quietEnd = parseInt(preferences.quietHoursEnd.split(':')[0]);
      
      if (quietStart > quietEnd) {
        // Quiet hours span midnight
        if (currentHour >= quietStart || currentHour < quietEnd) return false;
      } else {
        // Normal quiet hours
        if (currentHour >= quietStart && currentHour < quietEnd) return false;
      }
    }

    switch (template.trigger) {
      case 'step1_completed':
        return progress.step1OverviewCompleted && !progress.step2MyJourneyCompleted;
      
      case 'step2_completed':
        return progress.step2MyJourneyCompleted && !progress.step3OrgNarrativeCompleted;
      
      case 'step3_completed':
        return progress.step3OrgNarrativeCompleted && !progress.step4FractalsCompleted;
      
      case 'step4_completed':
        return progress.step4FractalsCompleted && !progress.step5SimulationCompleted;
      
      case 'step5_completed':
        return progress.step5SimulationCompleted && !progress.step6ActionFlowCompleted;
      
      case 'journey_complete':
        return progress.step6ActionFlowCompleted && progress.overallCompletionPercentage >= 100;
      
      case 'daily_encouragement':
        return daysSinceLastLogin === 1 && preferences?.encouragementMessages;
      
      case 'inactivity_3_days':
        return daysSinceLastLogin === 3 && preferences?.progressReminders;
      
      case 'inactivity_7_days':
        return daysSinceLastLogin === 7 && preferences?.encouragementMessages;
      
      case 'stuck_on_step':
        // If user has been on same step for more than 3 days
        return daysSinceLastLogin > 3 && progress.overallCompletionPercentage < 100;
      
      case 'life_component_tip':
        return progress.currentStep === 3 && !progress.step3OrgNarrativeCompleted && preferences?.tipOfTheDay;
      
      case 'money_component_tip':
        return progress.currentStep === 4 && !progress.step4FractalsCompleted && preferences?.tipOfTheDay;
      
      case 'joy_component_tip':
        return progress.currentStep === 5 && !progress.step5SimulationCompleted && preferences?.tipOfTheDay;
      
      default:
        return false;
    }
  };

  const triggerNotification = async (template: NotificationTemplate, progress: JourneyProgress) => {
    // Personalize the message if enabled
    let personalizedMessage = template.message;
    if (preferences?.personalizedMessaging && preferences?.useFirstName) {
      // This would typically get the user's first name from their profile
      // For now, we'll use a generic personalization
      personalizedMessage = template.message.replace(/You've/g, "You've");
    }

    // Adjust emotional tone based on preferences
    let adjustedTone = template.emotionalTone;
    if (preferences?.motivationalTone === 'professional') {
      adjustedTone = 'gentle';
    } else if (preferences?.motivationalTone === 'inspirational') {
      adjustedTone = 'celebratory';
    }

    const notificationData = {
      title: template.title,
      message: personalizedMessage,
      type: template.type,
      category: template.category,
      priority: template.priority,
      emotionalTone: adjustedTone,
      journeyStep: template.journeyStep,
      componentType: template.componentType,
      actionText: template.actionText,
      actionUrl: template.actionUrl,
      personalizedContent: {
        currentStep: progress.currentStep,
        completionPercentage: progress.overallCompletionPercentage,
        daysSinceStart: Math.floor((new Date().getTime() - new Date(progress.createdAt).getTime()) / (1000 * 60 * 60 * 24))
      }
    };

    try {
      await createNotificationMutation.mutateAsync(notificationData);
      
      // Track engagement
      await trackEngagementMutation.mutateAsync({
        engagementType: 'notification_triggered',
        journeyStep: progress.currentStep,
        componentType: template.componentType,
        details: {
          notificationType: template.type,
          trigger: template.trigger,
          emotionalTone: adjustedTone
        }
      });

      // Show toast for high priority notifications
      if (template.priority === 'high' || template.priority === 'urgent') {
        toast({
          title: template.title,
          description: personalizedMessage,
          action: template.actionText ? (
            <button 
              onClick={() => window.location.href = template.actionUrl || '/journey'}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              {template.actionText}
            </button>
          ) : undefined
        });
      }
    } catch (error) {
      console.error('Failed to trigger notification:', error);
    }
  };

  // Check for notification triggers when progress changes
  useEffect(() => {
    if (!progress || !preferences) return;

    const checkNotifications = () => {
      notificationTemplates.forEach(template => {
        if (shouldTriggerNotification(template, progress)) {
          // Add a small delay to prevent spam
          setTimeout(() => {
            triggerNotification(template, progress);
          }, template.delayMinutes ? template.delayMinutes * 60000 : 2000);
        }
      });
    };

    // Debounce notification checks
    const timeoutId = setTimeout(checkNotifications, 5000);
    return () => clearTimeout(timeoutId);
  }, [progress, preferences]);

  // This component doesn't render anything visible
  return null;
};

export default SmartNotificationTrigger;